// src/contexts/SsoCartContext.js
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import SsoServices from "../services/SsoServices";
import {
  ADD_TO_CART,
  REMOVE_FROM_CART,
  GET_CART_DETAILS,
} from "../constants/api-constants";
import { CartPageTypeEnum, entityTypeEnum, transactionType } from "../constants/common";

const SsoCartContext = createContext();

const CART_STORAGE_KEY = "sso_cart_items";

export const SsoCartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Failed to load cart from localStorage:", error);
      return [];
    }
  });

  const [cartMetadata, setCartMetadata] = useState({
    rowVersion: null,
    cartId: null,
    currency: null,
  });

  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      console.error("Failed to save cart to localStorage:", error);
    }
  }, [cart]);

  const isAddInPageType = (CartPageType) =>
    CartPageType === CartPageTypeEnum.ADDIN;

  const getDefaultSubscription = (item) =>
    item.defaultSubscription || item.subscriptionOptions?.[0];

  const isFreeCartItem = (item, defaultSub, CartPageType) => {
    if (isAddInPageType(CartPageType)) {
      return item?.costType === 1;
    }
    return !defaultSub && item.price === "$0";
  };

  const getTransactionTypeByPage = (CartPageType) =>
    isAddInPageType(CartPageType)
      ? transactionType.ADDIN_PURCHASE
      : transactionType.CONTENT_PURCHASE;

  const buildAddToCartPayload = (item, defaultSub, CartPageType) => {
    const transactionTypeValue = getTransactionTypeByPage(CartPageType);

    if (isFreeCartItem(item, defaultSub, CartPageType)) {
      return {
        itemId: item.id,
        quantity: 1,
        amount: 0,
        transactionType: transactionTypeValue,
        frequency: 99,
        currency: "USD",
        isRecurring: false,
      };
    }

    if (!defaultSub) {
      return null;
    }

    const amount = isAddInPageType(CartPageType)
      ? defaultSub.actualValue ?? defaultSub.baseValue ?? 0
      : defaultSub.value ?? 0;
    const frequency = isAddInPageType(CartPageType)
      ? defaultSub?.frequency
      : defaultSub?.type;

    return {
      itemId: item.id,
      quantity: 1,
      amount,
      transactionType: transactionTypeValue,
      frequency,
      currency: "USD",
      isRecurring: defaultSub.isRecurring || false,
    };
  };

  const addToCart = async (item, CartPageType = CartPageTypeEnum.CONTENT) => {
    try {
      const defaultSub = getDefaultSubscription(item);
      const payload = buildAddToCartPayload(item, defaultSub, CartPageType);

      if (!payload) {
        console.error(
          "No subscription available for this item and it's not free",
        );
        return { success: false, error: "No subscription available" };
      }

      const response = await SsoServices.ssoPost(ADD_TO_CART, payload);

      if (response.responseStatus?.isSuccess) {
        const cartData = response.result;
        const addedItem = cartData?.items?.find(
          (cartItem) => cartItem.itemId === item.id,
        );
        const itemWithCartData = {
          ...item,
          cartItemId: addedItem?.cartItemId,
          rowVersion: addedItem?.rowVersion,
        };
        setCart((prev) => [...prev, itemWithCartData]);

        // Update cart count from API response
        if (cartData?.items) {
          setCartCount(cartData.items.length);
        }

        return { success: true };
      }
      return { success: false, error: response.responseStatus?.error };
    } catch (error) {
      console.error("Add to cart error:", error);
      return { success: false, error: error.message };
    }
  };

  const removeFromCart = async (item, CartPageType = CartPageTypeEnum.CONTENT) => {
    try {
      // Use itemId if available, otherwise fall back to id
      const contentId = item.itemId || item.id || 0;
      const url = REMOVE_FROM_CART.replace("{contentId}", contentId);
      const payload = {
        transactionType: CartPageType === CartPageTypeEnum.ADDIN ? transactionType.ADDIN_PURCHASE : transactionType.CONTENT_PURCHASE,
        entityType: CartPageType === CartPageTypeEnum.ADDIN ? entityTypeEnum.ADDIN : entityTypeEnum.CONTENT,
      };
      const response = await SsoServices.ssoDelete(url, payload);
      if (response.responseStatus?.isSuccess) {
        setCart((prev) => prev.filter((c) => (c.itemId || c.id) !== contentId));

        // Update cart count from API response
        const cartData = response.result;
        if (cartData?.items) {
          setCartCount(cartData.items.length);
        }

        return { success: true };
      }
      console.error("Remove failed:", response.responseStatus?.error);
      return { success: false, error: response.responseStatus?.error };
    } catch (error) {
      console.error("Remove from cart error:", error);
      return { success: false, error: error.message };
    }
  };

  const toggleItem = (item) => {
    setCart((prev) => {
      const exists = prev.find((c) => c.id === item.id);
      if (exists) {
        return prev.filter((c) => c.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const clearInvalidItems = () => {
    setCart((prev) => prev.filter((item) => item.cartItemId));
  };

  const getCartDetails = async () => {
    try {
      const response = await SsoServices.ssoGet(GET_CART_DETAILS);
      if (response.responseStatus?.isSuccess) {
        const cartData = response.result;
        if (cartData?.items) {
          setCart(cartData.items);
          setCartCount(cartData.items.length);
          setCartMetadata({
            rowVersion: cartData.rowVersion,
            cartId: cartData.cartId,
            currency: cartData.currency,
          });
          return { success: true, data: cartData.items, metadata: cartData };
        }
        return { success: true, data: [] };
      }
      console.error("Get cart details failed:", response.responseStatus?.error);
      return { success: false, error: response.responseStatus?.error };
    } catch (error) {
      console.error("Get cart details error:", error);
      return { success: false, error: error.message };
    }
  };

  const value = useMemo(
    () => ({
      cart,
      cartMetadata,
      count: cartCount,
      setCartCount,
      addToCart,
      removeFromCart,
      toggleItem,
      clearCart,
      clearInvalidItems,
      getCartDetails,
    }),
    [cart, cartMetadata, cartCount],
  );

  return (
    <SsoCartContext.Provider value={value}>{children}</SsoCartContext.Provider>
  );
};

SsoCartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useSsoCart = () => {
  const context = useContext(SsoCartContext);
  if (!context) {
    console.warn("useSsoCart must be used within SsoCartProvider");
    return {
      cart: [],
      cartMetadata: { rowVersion: null, cartId: null, currency: null },
      cartCount: 0,
      addToCart: () => Promise.resolve(),
      removeFromCart: () => Promise.resolve(),
      getCartDetails: () => Promise.resolve(),
      updateCartItemSubscription: () => Promise.resolve(),
    };
  }
  return context;
};

// Export the context object 
export { SsoCartContext };
